import React, { useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Sparkles, ArrowUpRight } from "lucide-react";
import api, { BRAND, formatApiErrorDetail } from "../../lib/api";
import { Card, Button } from "../../components/ui-kit";

// Schermata mostrata al posto del modulo di lavoro quando non è compreso nel servizio attivo:
// il cliente non deve mai vedere la schermata di lavoro reale di un modulo bloccato.
export function LockedModuleNotice({ modKey, label, planLabel, phase2 }) {
  const outlet = useOutletContext() || {};
  const [sending, setSending] = useState(false);
  const requestActivation = async () => {
    setSending(true);
    try {
      await api.post("/app/activation-requests", { module: modKey });
      const companyName = outlet.ctx?.tenant?.name;
      const text = `Ciao, vorrei attivare la funzione "${label}"${companyName ? ` per ${companyName}` : ""} su DF HACCP.`;
      window.open(`https://wa.me/${BRAND.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
      toast.success("Richiesta inviata al consulente");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <div className="max-w-2xl mx-auto" data-testid="module-locked-notice">
      <Card className="p-10 text-center">
        <div className="w-14 h-14 rounded-2xl bg-navy grid place-items-center mx-auto mb-5">
          {phase2 ? <Sparkles className="w-7 h-7 text-gold-bright" /> : <Lock className="w-7 h-7 text-gold-bright" />}
        </div>
        <h1 className="font-heading text-2xl font-bold">{label}</h1>
        {phase2 ? (
          <p className="text-slate-500 font-body mt-3">
            Questa funzione è inclusa nel livello di consulenza configurato ed è predisposta nell'architettura. Sarà attivata in una
            fase successiva del rilascio. Il consulente ti avviserà alla disponibilità.
          </p>
        ) : (
          <>
            <p className="text-slate-500 font-body mt-3">
              <strong>Funzione non compresa nel servizio attivo.</strong> Questo modulo non è incluso nel livello di consulenza attuale
              {planLabel ? ` (disponibile con ${planLabel})` : ""}. Richiedi l'attivazione al tuo consulente.
            </p>
            <div className="mt-6">
              <Button variant="accent" loading={sending} onClick={requestActivation} data-testid="request-activation">
                <ArrowUpRight className="w-4 h-4" /> Richiedi attivazione
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// Blocca l'accesso a un modulo di lavoro quando non è attivo per il tenant: usato avvolgendo
// direttamente la route del modulo, così il componente reale non viene mai montato/renderizzato.
export function ModuleGate({ modKey, children }) {
  const { modMap, isLocked } = useOutletContext();
  if (!isLocked(modKey)) return children;
  const m = modMap[modKey];
  const phase2 = m && m.active && !m.available_phase1;
  return <LockedModuleNotice modKey={modKey} label={m?.label || "Funzione"} planLabel={m?.plan_label} phase2={phase2} />;
}

// Modulo non attivo/non ancora disponibile nel livello di consulenza configurato (route generica /app/m/:modKey).
export default function ModulePlaceholder() {
  const { modKey } = useParams();
  const { modMap } = useOutletContext();
  const m = modMap[modKey];
  const label = m?.label || "Funzione";
  const phase2 = m && m.active && !m.available_phase1;
  return <LockedModuleNotice modKey={modKey} label={label} planLabel={m?.plan_label} phase2={phase2} />;
}
